import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Mic, Square, Loader2, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";

export function VoiceMicButton({ onText, disabled }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        setState("uploading");
        try {
          const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const r = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data?.text) { onText(r.data.text.trim()); toast.success("Captured"); }
          else toast.error("Couldn't transcribe");
        } catch { toast.error("Couldn't transcribe"); }
        finally {
          setState("idle");
          streamRef.current?.getTracks().forEach(t => t.stop());
        }
      };
      rec.start();
      recRef.current = rec;
      setState("recording");
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => { recRef.current?.stop(); recRef.current = null; };

  return (
    <button type="button" disabled={disabled || state === "uploading"} data-testid="voice-mic-btn"
      onClick={state === "recording" ? stop : start}
      title={state === "recording" ? "Stop & transcribe" : "Speak to fill"}
      className={`flex items-center justify-center w-9 h-9 rounded-xl border transition-all ${
        state === "recording" ? "bg-[#C76B58] text-white border-[#C76B58] animate-pulse"
        : "bg-[#F9F8F6] text-[#4A6750] border-[#E5E7E4] hover:border-[#4A6750]"
      }`}>
      {state === "uploading" ? <Loader2 className="w-4 h-4 animate-spin" />
        : state === "recording" ? <Square className="w-4 h-4" />
        : <Mic className="w-4 h-4" strokeWidth={1.75} />}
    </button>
  );
}

export function AIHelpButton({ field, context }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const ask = async () => {
    if (text || loading) return;
    setLoading(true);
    try {
      const r = await api.post("/ai/field-help", { field, context });
      setText(r.data.reply || "");
    } catch { setText("Sorry, I couldn't fetch the explanation."); }
    finally { setLoading(false); }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) ask(); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="ai-help-btn"
          className="text-[#7D8F84] hover:text-[#4A6750] transition-colors p-1 rounded-md">
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 rounded-xl border border-[#E5E7E4] shadow-md text-sm leading-relaxed bg-white">
        <p className="text-xs font-semibold tracking-[0.1em] uppercase text-[#4A6750] mb-1.5">AI Help</p>
        {loading ? <p className="text-[#7D8F84] italic">Thinking…</p>
          : <p className="text-[#1C2E24] whitespace-pre-wrap">{text}</p>}
      </PopoverContent>
    </Popover>
  );
}

/** Input + voice + ai-help all in one. */
export default function VoiceInput({ label, value, onChange, fieldKey, context, placeholder, type = "text", testId, required, inputMode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">{label}</Label>
        <AIHelpButton field={label} context={context} />
      </div>
      <div className="flex gap-2">
        <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} inputMode={inputMode} required={required}
          data-testid={testId} className="bg-[#F9F8F6] rounded-xl flex-1" />
        <VoiceMicButton onText={(t) => onChange(value ? `${value} ${t}` : t)} />
      </div>
    </div>
  );
}
